"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  EyeOff,
  ShieldCheck,
  AlertTriangle,
  Upload,
  Download,
  Copy,
  Check,
  Undo2,
  Trash2,
  Sparkles,
  FileSearch,
  ClipboardPaste,
} from "lucide-react";

type RedactionStyle = "solid" | "noise" | "pixelate_unsafe";
type ExportFormat = "image/webp" | "image/png" | "image/jpeg";

interface MetadataSegment {
  kind: string;
  byteLength: number;
  detail: string;
  isGps?: boolean;
}

interface RedactionBox {
  id: string;
  x: number; // normalized 0..1
  y: number;
  w: number;
  h: number;
  jitterLeft: number; // deterministic random padding 0..1
  jitterRight: number;
  noiseSeed: number;
}

interface LoadedDocument {
  name: string;
  originalBytes: number;
  origWidth: number;
  origHeight: number;
  metadata: MetadataSegment[];
  img: HTMLImageElement;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function extractPrintableHints(slice: Uint8Array, maxHints = 3): string[] {
  const hints: string[] = [];
  let cur = "";
  for (let i = 0; i < Math.min(slice.length, 4096); i++) {
    const c = slice[i];
    if (c >= 32 && c <= 126) {
      cur += String.fromCharCode(c);
    } else {
      const trimmed = cur.trim();
      if (
        trimmed.length >= 5 &&
        trimmed !== "Exif" &&
        /[A-Za-z]/.test(trimmed) &&
        !hints.includes(trimmed)
      ) {
        hints.push(trimmed.slice(0, 42));
        if (hints.length >= maxHints) break;
      }
      cur = "";
    }
  }
  if (cur.trim().length >= 5 && hints.length < maxHints) {
    hints.push(cur.trim().slice(0, 42));
  }
  return hints;
}

function hasGpsTagInExif(slice: Uint8Array): boolean {
  for (let i = 0; i < slice.length - 3; i++) {
    // ASCII "GPS" or TIFF GPSInfo IFD pointer tag 0x8825 (big-endian or little-endian)
    if (slice[i] === 0x47 && slice[i + 1] === 0x50 && slice[i + 2] === 0x53) return true;
    if (
      (slice[i] === 0x88 && slice[i + 1] === 0x25 && slice[i + 2] === 0x00 && slice[i + 3] === 0x04) ||
      (slice[i] === 0x25 && slice[i + 1] === 0x88 && slice[i + 2] === 0x04 && slice[i + 3] === 0x00)
    ) {
      return true;
    }
  }
  return false;
}

export function inspectBinaryImageMetadata(bytes: Uint8Array): MetadataSegment[] {
  const found: MetadataSegment[] = [];
  if (bytes.length < 12) return found;

  // 1. JPEG (FF D8)
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (bytes[offset] !== 0xff) {
        offset++;
        continue;
      }
      while (offset < bytes.length && bytes[offset] === 0xff) offset++;
      if (offset >= bytes.length) break;
      const marker = bytes[offset];
      offset++;

      // SOS (Start of Scan) or EOI
      if (marker === 0xda || marker === 0xd9) break;
      if (marker === 0x00 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > bytes.length) break;

      const segLen = (bytes[offset] << 8) | bytes[offset + 1];
      if (segLen < 2 || offset + segLen > bytes.length) break;
      const payload = bytes.subarray(offset + 2, offset + segLen);

      if (marker === 0xe1) {
        const isXmp =
          payload.length > 10 &&
          new TextDecoder().decode(payload.subarray(0, 28)).includes("ns.adobe.com");
        const gps = hasGpsTagInExif(payload);
        const hints = extractPrintableHints(payload);
        found.push({
          kind: isXmp ? "JPEG XMP-Paket (APP1)" : "JPEG EXIF-Block (APP1)",
          byteLength: segLen,
          detail:
            hints.length > 0
              ? `Gefundene Einträge: ${hints.join(" · ")}`
              : "Kameramodell, Zeitstempel oder Aufnahmeparameter",
          isGps: gps,
        });
      } else if (marker === 0xed) {
        found.push({
          kind: "IPTC / Photoshop-Block (APP13)",
          byteLength: segLen,
          detail: "Autoren-, Bearbeitungs- oder Urheberrechtsdaten",
        });
      } else if (marker === 0xfe) {
        const hints = extractPrintableHints(payload, 1);
        found.push({
          kind: "JPEG-Kommentar (COM)",
          byteLength: segLen,
          detail: hints[0] ? `Kommentar: „${hints[0]}“` : "Eingebetteter Freitext-Kommentar",
        });
      }

      offset += segLen;
    }
    return found;
  }

  // 2. PNG (89 50 4E 47)
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    let offset = 8;
    while (offset + 12 <= bytes.length) {
      const len =
        ((bytes[offset] << 24) |
          (bytes[offset + 1] << 16) |
          (bytes[offset + 2] << 8) |
          bytes[offset + 3]) >>>
        0;
      const type = String.fromCharCode(
        bytes[offset + 4],
        bytes[offset + 5],
        bytes[offset + 6],
        bytes[offset + 7]
      );
      if (offset + 12 + len > bytes.length) break;
      const payload = bytes.subarray(offset + 8, offset + 8 + len);

      if (type === "eXIf" || type === "tEXt" || type === "iTXt" || type === "zTXt" || type === "tIME") {
        const hints = extractPrintableHints(payload, 2);
        found.push({
          kind: `PNG ${type}-Chunk`,
          byteLength: len + 12,
          detail:
            type === "tIME"
              ? "Letzter Bearbeitungszeitstempel der Datei"
              : hints.length > 0
                ? `Text-Metadaten: ${hints.join(" · ")}`
                : "Eingebettete Software- oder EXIF-Metadaten",
          isGps: type === "eXIf" && hasGpsTagInExif(payload),
        });
      }
      if (type === "IEND") break;
      offset += 12 + len;
    }
    return found;
  }

  // 3. WebP (RIFF....WEBP)
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45
  ) {
    let offset = 12;
    while (offset + 8 <= bytes.length) {
      const fourCc = String.fromCharCode(
        bytes[offset],
        bytes[offset + 1],
        bytes[offset + 2],
        bytes[offset + 3]
      );
      const len =
        (bytes[offset + 4] |
          (bytes[offset + 5] << 8) |
          (bytes[offset + 6] << 16) |
          (bytes[offset + 7] << 24)) >>>
        0;
      if (fourCc === "EXIF" || fourCc === "XMP ") {
        const payload = bytes.subarray(offset + 8, Math.min(bytes.length, offset + 8 + len));
        found.push({
          kind: `WebP ${fourCc.trim()}-Chunk`,
          byteLength: len + 8,
          detail: "Eingebettete Kamera- oder Bearbeitungs-Metadaten",
          isGps: hasGpsTagInExif(payload),
        });
      }
      offset += 8 + len + (len % 2);
    }
  }

  return found;
}

function injectDemoExifIntoJpeg(jpegBytes: Uint8Array): Uint8Array {
  if (jpegBytes.length < 4 || jpegBytes[0] !== 0xff || jpegBytes[1] !== 0xd8) return jpegBytes;
  const encoder = new TextEncoder();
  const payloadStr =
    "Exif\0\0Apple iPhone 16 Pro\0Software: macOS Screenshot 15.2\0GPSLatitude: 53.5511 N, 9.9937 E (Hamburg)\0DateTimeOriginal: 2026:10:26 19:42:11\0";
  const payload = encoder.encode(payloadStr);
  const segLen = payload.length + 2;
  const app1 = new Uint8Array(4 + payload.length);
  app1[0] = 0xff;
  app1[1] = 0xe1;
  app1[2] = (segLen >> 8) & 0xff;
  app1[3] = segLen & 0xff;
  app1.set(payload, 4);

  const out = new Uint8Array(2 + app1.length + (jpegBytes.length - 2));
  out[0] = 0xff;
  out[1] = 0xd8;
  out.set(app1, 2);
  out.set(jpegBytes.subarray(2), 2 + app1.length);
  return out;
}

async function createDemoScreenshotDoc(): Promise<{
  doc: LoadedDocument;
  initialBoxes: RedactionBox[];
}> {
  const width = 920;
  const height = 520;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  // Realistic light UI screenshot background
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, width, height);

  // Window header bar
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, width, 54);
  ctx.fillStyle = "#ef4444";
  ctx.beginPath();
  ctx.arc(26, 27, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#f59e0b";
  ctx.beginPath();
  ctx.arc(46, 27, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#10b981";
  ctx.beginPath();
  ctx.arc(66, 27, 6, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#e2e8f0";
  ctx.font = "bold 15px sans-serif";
  ctx.fillText("Kundenportal · Abrechnung & API-Zugang (Beispiel-Screenshot)", 96, 32);

  // Card 1: Invoice & IBAN
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#cbd5e1";
  ctx.lineWidth = 1.5;
  ctx.fillRect(40, 84, 840, 185);
  ctx.strokeRect(40, 84, 840, 185);

  ctx.fillStyle = "#0f172a";
  ctx.font = "bold 20px sans-serif";
  ctx.fillText("Rechnung #RE-2026-4092 · Hosting & Infrastruktur", 68, 124);

  ctx.fillStyle = "#475569";
  ctx.font = "15px sans-serif";
  ctx.fillText("Rechnungsempfänger:", 68, 164);
  ctx.fillText("SEPA-Lastschrift IBAN:", 68, 202);
  ctx.fillText("Privatadresse:", 68, 240);

  ctx.fillStyle = "#0f172a";
  ctx.font = "bold 16px monospace";
  ctx.fillText("Max Mustermann GmbH", 280, 164);
  ctx.fillText("DE89 3704 0044 0532 0130 00", 280, 202);
  ctx.fillText("Hafenstraße 42, 20457 Hamburg", 280, 240);

  // Card 2: Production API Key
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(40, 294, 840, 185);
  ctx.strokeRect(40, 294, 840, 185);

  ctx.fillStyle = "#0f172a";
  ctx.font = "bold 18px sans-serif";
  ctx.fillText("Produktive Zugangsdaten (Vor dem Ticket-Versand schwärzen!)", 68, 334);

  ctx.fillStyle = "#475569";
  ctx.font = "15px sans-serif";
  ctx.fillText("Public Project ID:", 68, 376);
  ctx.fillText("Live Secret Token:", 68, 418);
  ctx.fillText("Ziehe mit der Maus oder dem Finger weitere Rechtecke über beliebige Stellen!", 68, 456);

  ctx.fillStyle = "#059669";
  ctx.font = "16px monospace";
  ctx.fillText("proj_public_88412a", 280, 376);
  ctx.fillStyle = "#dc2626";
  ctx.font = "bold 16px monospace";
  ctx.fillText("sk_live_99481c0f82a74b19e3d5510fa", 280, 418);

  const rawJpegBlob = await new Promise<Blob>((resolve) => {
    canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.92);
  });
  const rawBuf = new Uint8Array(await rawJpegBlob.arrayBuffer());
  const withExif = injectDemoExifIntoJpeg(rawBuf);
  const metadata = inspectBinaryImageMetadata(withExif);

  const url = URL.createObjectURL(new Blob([withExif.slice()], { type: "image/jpeg" }));
  const img = await new Promise<HTMLImageElement>((resolve) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.src = url;
  });
  URL.revokeObjectURL(url);

  return {
    doc: {
      name: "abrechnung-screenshot-iphone.jpg",
      originalBytes: withExif.length,
      origWidth: width,
      origHeight: height,
      metadata,
      img,
    },
    initialBoxes: [
      {
        id: "demo-iban",
        x: 274 / width,
        y: 182 / height,
        w: 295 / width,
        h: 28 / height,
        jitterLeft: 0.4,
        jitterRight: 0.7,
        noiseSeed: 17,
      },
      {
        id: "demo-secret",
        x: 274 / width,
        y: 398 / height,
        w: 350 / width,
        h: 28 / height,
        jitterLeft: 0.6,
        jitterRight: 0.5,
        noiseSeed: 53,
      },
    ],
  };
}

function drawRedactedCanvas(
  canvas: HTMLCanvasElement,
  doc: LoadedDocument,
  boxes: RedactionBox[],
  draftBox: { x: number; y: number; w: number; h: number } | null,
  style: RedactionStyle,
  solidColor: string,
  jitterEnabled: boolean,
  maxExportWidth: number
) {
  const scale = maxExportWidth > 0 ? Math.min(1, maxExportWidth / doc.origWidth) : 1;
  const outW = Math.max(1, Math.round(doc.origWidth * scale));
  const outH = Math.max(1, Math.round(doc.origHeight * scale));

  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return;

  ctx.drawImage(doc.img, 0, 0, outW, outH);

  const applyBox = (b: RedactionBox) => {
    const padL = jitterEnabled ? Math.round(b.jitterLeft * 6) : 0;
    const padR = jitterEnabled ? Math.round(b.jitterRight * 6) : 0;
    const rx = Math.max(0, Math.floor(b.x * outW) - padL);
    const ry = Math.max(0, Math.floor(b.y * outH));
    const rw = Math.min(outW - rx, Math.ceil(b.w * outW) + padL + padR);
    const rh = Math.min(outH - ry, Math.ceil(b.h * outH));
    if (rw <= 1 || rh <= 1) return;

    if (style === "solid") {
      ctx.fillStyle = solidColor;
      ctx.fillRect(rx, ry, rw, rh);
      return;
    }

    if (style === "noise") {
      const imgData = ctx.createImageData(rw, rh);
      const data = imgData.data;
      let s = (b.noiseSeed * 2654435761) >>> 0;
      const cell = 3;
      for (let py = 0; py < rh; py += cell) {
        for (let px = 0; px < rw; px += cell) {
          s = (s * 1664525 + 1013904223) >>> 0;
          const shade = 20 + (s % 55);
          for (let dy = 0; dy < cell && py + dy < rh; dy++) {
            for (let dx = 0; dx < cell && px + dx < rw; dx++) {
              const idx = ((py + dy) * rw + (px + dx)) * 4;
              data[idx] = shade;
              data[idx + 1] = shade;
              data[idx + 2] = shade;
              data[idx + 3] = 255;
            }
          }
        }
      }
      ctx.putImageData(imgData, rx, ry);
      return;
    }

    // Demo-only unsafe mosaic mode (shows visually why block averages leak character shapes!)
    const srcData = ctx.getImageData(rx, ry, rw, rh);
    const d = srcData.data;
    const block = Math.max(8, Math.round(12 * scale));
    for (let py = 0; py < rh; py += block) {
      for (let px = 0; px < rw; px += block) {
        let rSum = 0,
          gSum = 0,
          bSum = 0,
          cnt = 0;
        for (let dy = 0; dy < block && py + dy < rh; dy++) {
          for (let dx = 0; dx < block && px + dx < rw; dx++) {
            const idx = ((py + dy) * rw + (px + dx)) * 4;
            rSum += d[idx];
            gSum += d[idx + 1];
            bSum += d[idx + 2];
            cnt++;
          }
        }
        const rAvg = Math.round(rSum / cnt);
        const gAvg = Math.round(gSum / cnt);
        const bAvg = Math.round(bSum / cnt);
        for (let dy = 0; dy < block && py + dy < rh; dy++) {
          for (let dx = 0; dx < block && px + dx < rw; dx++) {
            const idx = ((py + dy) * rw + (px + dx)) * 4;
            d[idx] = rAvg;
            d[idx + 1] = gAvg;
            d[idx + 2] = bAvg;
          }
        }
      }
    }
    ctx.putImageData(srcData, rx, ry);
  };

  boxes.forEach(applyBox);

  if (draftBox && draftBox.w > 0.002 && draftBox.h > 0.002) {
    const dx = draftBox.x * outW;
    const dy = draftBox.y * outH;
    const dw = draftBox.w * outW;
    const dh = draftBox.h * outH;
    ctx.fillStyle = "rgba(16, 185, 129, 0.28)";
    ctx.fillRect(dx, dy, dw, dh);
    ctx.strokeStyle = "#10b981";
    ctx.lineWidth = 2;
    ctx.strokeRect(dx, dy, dw, dh);
  }
}

export default function ImageRedactorPlayground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);

  const [doc, setDoc] = useState<LoadedDocument | null>(null);
  const [boxes, setBoxes] = useState<RedactionBox[]>([]);
  const [draftBox, setDraftBox] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null
  );

  const [redactionStyle, setRedactionStyle] = useState<RedactionStyle>("solid");
  const [solidColor, setSolidColor] = useState<string>("#09090b");
  const [jitterEnabled, setJitterEnabled] = useState<boolean>(true);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("image/webp");
  const [maxExportWidth, setMaxExportWidth] = useState<number>(0);

  const [exportBlob, setExportBlob] = useState<Blob | null>(null);
  const [copiedImg, setCopiedImg] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadDemo = useCallback(async () => {
    setLoadError(null);
    const { doc: demoDoc, initialBoxes } = await createDemoScreenshotDoc();
    setDoc(demoDoc);
    setBoxes(initialBoxes);
  }, []);

  useEffect(() => {
    let active = true;
    createDemoScreenshotDoc().then(({ doc: demoDoc, initialBoxes }) => {
      if (active) {
        setDoc(demoDoc);
        setBoxes(initialBoxes);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const loadUserFile = useCallback(async (file?: File | null) => {
    if (!file) return;
    setLoadError(null);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const metadata = inspectBinaryImageMetadata(buf);
      const url = URL.createObjectURL(file);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("Bilddatei konnte nicht gelesen werden."));
        el.src = url;
      });
      URL.revokeObjectURL(url);

      setDoc({
        name: file.name || "zwischenablage-screenshot.png",
        originalBytes: buf.length,
        origWidth: img.naturalWidth || img.width,
        origHeight: img.naturalHeight || img.height,
        metadata,
        img,
      });
      setBoxes([]);
    } catch {
      setLoadError("Bitte wähle eine gültige PNG-, JPEG- oder WebP-Bilddatei aus.");
    }
  }, []);

  // Support Ctrl+V / Cmd+V screenshot paste anywhere while tool is open!
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const pastedFile = items[i].getAsFile();
          if (pastedFile) {
            e.preventDefault();
            void loadUserFile(pastedFile);
            break;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [loadUserFile]);

  // Redraw canvas whenever document, boxes, draft, or settings change
  useEffect(() => {
    if (!doc || !canvasRef.current) return;
    const canvas = canvasRef.current;
    drawRedactedCanvas(
      canvas,
      doc,
      boxes,
      draftBox,
      redactionStyle,
      solidColor,
      jitterEnabled,
      maxExportWidth
    );

    if (draftBox) return;
    let cancelled = false;
    canvas.toBlob(
      (blob) => {
        if (!cancelled && blob) setExportBlob(blob);
      },
      exportFormat,
      0.86
    );
    return () => {
      cancelled = true;
    };
  }, [doc, boxes, draftBox, redactionStyle, solidColor, jitterEnabled, exportFormat, maxExportWidth]);

  const getNormalizedCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return { x, y };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = getNormalizedCoords(e);
    dragStartRef.current = pt;
    setDraftBox({ x: pt.x, y: pt.y, w: 0, h: 0 });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragStartRef.current) return;
    const pt = getNormalizedCoords(e);
    const x = Math.min(dragStartRef.current.x, pt.x);
    const y = Math.min(dragStartRef.current.y, pt.y);
    const w = Math.abs(pt.x - dragStartRef.current.x);
    const h = Math.abs(pt.y - dragStartRef.current.y);
    setDraftBox({ x, y, w, h });
  };

  const handlePointerUp = () => {
    if (draftBox && draftBox.w > 0.008 && draftBox.h > 0.008) {
      const rand = new Uint32Array(3);
      crypto.getRandomValues(rand);
      setBoxes((prev) => [
        ...prev,
        {
          id: `box-${Date.now()}-${rand[0] % 1000}`,
          x: draftBox.x,
          y: draftBox.y,
          w: draftBox.w,
          h: draftBox.h,
          jitterLeft: (rand[0] % 100) / 100,
          jitterRight: (rand[1] % 100) / 100,
          noiseSeed: (rand[2] % 997) + 1,
        },
      ]);
    }
    dragStartRef.current = null;
    setDraftBox(null);
  };

  const handleDownloadClean = () => {
    if (!exportBlob || !doc) return;
    const ext =
      exportFormat === "image/png" ? "png" : exportFormat === "image/jpeg" ? "jpg" : "webp";
    const base = doc.name.replace(/\.[^.]+$/, "") || "screenshot";
    const url = URL.createObjectURL(exportBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${base}-geschwaerzt.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    if (!canvasRef.current || !navigator.clipboard?.write) return;
    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvasRef.current!.toBlob((b) => resolve(b), "image/png");
    });
    if (!pngBlob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
      setCopiedImg(true);
      setTimeout(() => setCopiedImg(false), 1800);
    } catch {
      setLoadError("Zwischenablage-Bildkopie wird von diesem Browser blockiert – nutze den Download.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Top Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-[40px] cursor-pointer items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white hover:bg-emerald-700">
            <Upload className="h-3.5 w-3.5" />
            <span>Eigenes Bild / Screenshot öffnen</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => void loadUserFile(e.target.files?.[0])}
              className="sr-only"
            />
          </label>

          <span className="hidden items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-2 text-xs font-medium text-neutral-600 sm:inline-flex dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            <ClipboardPaste className="h-3.5 w-3.5 text-emerald-500" />
            <span>Oder direkt per Strg+V einfügen!</span>
          </span>

          <button
            type="button"
            onClick={() => void loadDemo()}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
          >
            <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
            <span>Demo-Rechnung (mit EXIF &amp; GPS)</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={boxes.length === 0}
            onClick={() => setBoxes((prev) => prev.slice(0, -1))}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span>Letzten Balken zurück ({boxes.length})</span>
          </button>

          <button
            type="button"
            disabled={boxes.length === 0}
            onClick={() => setBoxes([])}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>Alle löschen</span>
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs font-medium text-amber-900 dark:text-amber-200">
          {loadError}
        </div>
      )}

      {/* Main 2-Column Workbench: Interactive Redaction Canvas (Left) + Metadata & Export Panel (Right) */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void loadUserFile(e.dataTransfer.files?.[0]);
          }}
          className="flex flex-col gap-2.5 lg:col-span-8"
        >
          <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
            <span className="inline-flex items-center gap-1.5 font-medium text-neutral-700 dark:text-neutral-200">
              <EyeOff className="h-3.5 w-3.5 text-emerald-500" />
              <span>Ziehe mit Maus oder Finger ein Rechteck über sensible Stellen</span>
            </span>
            {doc && (
              <span className="font-mono">
                {doc.origWidth} × {doc.origHeight} px
              </span>
            )}
          </div>

          <div className="flex min-h-[340px] items-center justify-center overflow-hidden rounded-2xl border-2 border-neutral-200/90 bg-neutral-900/95 p-2 sm:p-3 dark:border-neutral-800">
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              className="max-h-[540px] w-auto max-w-full cursor-crosshair rounded-lg shadow-lg touch-none select-none"
            />
          </div>

          {redactionStyle === "pixelate_unsafe" && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/50 bg-amber-500/15 p-3 text-xs text-amber-950 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <strong>Sicherheitswarnung (Demo-Modus „Verpixelung“ aktiv):</strong> Mosaik-Filter
                mitteln lediglich die Helligkeit pro Block. Werkzeuge wie <em>Unredacter</em> oder
                Deconvolution-Angriffe können daraus bei bekannten Systemschriften Kontonummern und
                Passwörter zurückrechnen! Schalte vor dem Export auf <strong>Vollblock</strong> oder{" "}
                <strong>Rausch-Raster</strong> zurück.
              </div>
            </div>
          )}
        </div>

        {/* Right Controls: Security Method, EXIF Inspector, & Clean Export */}
        <div className="flex flex-col justify-between gap-4 rounded-2xl border border-neutral-200/90 bg-white p-4 lg:col-span-4 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="space-y-4">
            {/* 1. Redaction Mode */}
            <div>
              <div className="mb-1.5 text-xs font-bold text-neutral-800 dark:text-neutral-200">
                1. Schwärzungs-Verfahren
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {[
                  {
                    id: "solid" as const,
                    label: "Massiver Vollblock (100 % sicher)",
                    desc: "Überschreibt alle Farbkanäle restlos",
                  },
                  {
                    id: "noise" as const,
                    label: "Kryptografisches Rausch-Raster (100 % sicher)",
                    desc: "Zufallspixel ohne Rückschluss auf Originaltext",
                  },
                  {
                    id: "pixelate_unsafe" as const,
                    label: "⚠️ Mosaik-Verpixelung (Angreifbar – Vergleich)",
                    desc: "Zeigt, warum Block-Mittelwerte unsicher sind",
                  },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setRedactionStyle(m.id)}
                    className={`rounded-xl border px-3 py-2 text-left transition-colors cursor-pointer ${
                      redactionStyle === m.id
                        ? m.id === "pixelate_unsafe"
                          ? "border-amber-500 bg-amber-500/15 text-amber-950 dark:text-amber-200"
                          : "border-emerald-500 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200"
                        : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300"
                    }`}
                  >
                    <div className="text-xs font-bold">{m.label}</div>
                    <div className="text-[11px] opacity-75">{m.desc}</div>
                  </button>
                ))}
              </div>

              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
                {redactionStyle === "solid" && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-neutral-500">Balkenfarbe:</span>
                    {[
                      { label: "Schwarz", hex: "#09090b" },
                      { label: "Weiß", hex: "#ffffff" },
                      { label: "Schiefer", hex: "#334155" },
                    ].map((c) => (
                      <button
                        key={c.hex}
                        type="button"
                        onClick={() => setSolidColor(c.hex)}
                        className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold cursor-pointer ${
                          solidColor === c.hex
                            ? "border-emerald-500 ring-1 ring-emerald-500"
                            : "border-neutral-300 dark:border-neutral-700"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}

                <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                  <input
                    type="checkbox"
                    checked={jitterEnabled}
                    onChange={(e) => setJitterEnabled(e.target.checked)}
                    className="accent-emerald-600"
                  />
                  <span>Zufalls-Randversatz gegen Wortlängen-Analyse</span>
                </label>
              </div>
            </div>

            {/* 2. Binary EXIF / GPS Audit */}
            <div className="rounded-xl border border-neutral-200/90 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-950/70">
              <div className="mb-1.5 flex items-center justify-between text-xs font-bold text-neutral-800 dark:text-neutral-200">
                <span className="inline-flex items-center gap-1.5">
                  <FileSearch className="h-3.5 w-3.5 text-emerald-500" />
                  <span>2. Gefundene Datei-Metadaten</span>
                </span>
                <span className="font-mono text-[11px] text-neutral-500">
                  {doc ? `${doc.metadata.length} Blöcke` : "–"}
                </span>
              </div>

              {doc && doc.metadata.length > 0 ? (
                <ul className="space-y-1.5 text-[11px]">
                  {doc.metadata.map((seg, i) => (
                    <li
                      key={`${seg.kind}-${i}`}
                      className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-amber-950 dark:text-amber-200"
                    >
                      <div className="flex items-center justify-between font-bold">
                        <span>
                          {seg.kind} {seg.isGps ? "· 📍 GPS erkannt!" : ""}
                        </span>
                        <span className="font-mono">{formatSize(seg.byteLength)}</span>
                      </div>
                      <div className="mt-0.5 truncate opacity-85">{seg.detail}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-neutral-500">
                  Keine eingebetteten EXIF-/Text-Segmente in der Quelldatei gefunden.
                </p>
              )}

              <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                <span>Im Export restlos entfernt (0 EXIF-/GPS-Bytes)</span>
              </div>
            </div>

            {/* 3. Format & Resolution */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label
                  htmlFor="redact-fmt"
                  className="mb-1 block text-[11px] font-bold text-neutral-700 dark:text-neutral-300"
                >
                  3. Zielformat
                </label>
                <select
                  id="redact-fmt"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                  className="min-h-[38px] w-full rounded-xl border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs font-semibold dark:border-neutral-700 dark:bg-neutral-800"
                >
                  <option value="image/webp">WebP (kompakt)</option>
                  <option value="image/png">PNG (verlustfrei)</option>
                  <option value="image/jpeg">JPEG</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="redact-width"
                  className="mb-1 block text-[11px] font-bold text-neutral-700 dark:text-neutral-300"
                >
                  Max. Breite
                </label>
                <select
                  id="redact-width"
                  value={maxExportWidth}
                  onChange={(e) => setMaxExportWidth(Number(e.target.value))}
                  className="min-h-[38px] w-full rounded-xl border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs font-semibold dark:border-neutral-700 dark:bg-neutral-800"
                >
                  <option value={0}>Originalgröße</option>
                  <option value={1600}>Max. 1600 px</option>
                  <option value={1080}>Max. 1080 px</option>
                </select>
              </div>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="space-y-2 border-t border-neutral-200/80 pt-3 dark:border-neutral-800">
            {doc && exportBlob && (
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>Dateigröße vorher → nachher:</span>
                <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                  {formatSize(doc.originalBytes)} → {formatSize(exportBlob.size)}
                </span>
              </div>
            )}

            <button
              type="button"
              disabled={!exportBlob || redactionStyle === "pixelate_unsafe"}
              onClick={handleDownloadClean}
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
            >
              <Download className="h-4 w-4" />
              <span>Bereinigtes Bild herunterladen</span>
            </button>

            <button
              type="button"
              disabled={!exportBlob || redactionStyle === "pixelate_unsafe"}
              onClick={() => void handleCopyToClipboard()}
              className="inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 disabled:opacity-45 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
            >
              {copiedImg ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                  <span>In Zwischenablage kopiert (Strg+V bereit)!</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span>Direkt in Zwischenablage kopieren</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
